import React from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4plugins_rangeSelector from '@amcharts/amcharts4/plugins/rangeSelector';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { connect } from 'react-redux';
import { formatData } from './helpers';
import { withTranslation } from 'react-i18next';
import exportIcon from './download-icon.svg';

am4core.useTheme(am4themes_animated);
const dateFormat = 'MM/dd/yyyy h:mm a';

function Chart({ t, ...props }) {
  const data = props.report.statisticsChart;
  let chart;
  React.useEffect(() => {
    chart = am4core.create('chartArea', am4charts.XYChart);
    drawChart();
    return () => {
      if (chart) {
        chart.dispose();
      }
    };
  });
  function drawChart() {
    var interfaceColors = new am4core.InterfaceColorSet();
    chart.dateFormatter.inputDateFormat = 'YYYY-MM-DDTHH:mm:ssZ';
    chart.dateFormatter.dateFormat = dateFormat;
    chart.dateFormatter.utc = true;
    chart.data = formatData(data);
    // the following line makes value axes to be arranged vertically.
    chart.leftAxesContainer.layout = 'vertical';

    let dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.groupData = true;
    dateAxis.renderer.labels.template.rotation = 90;
    dateAxis.renderer.labels.template.verticalCenter = 'middle';
    dateAxis.renderer.labels.template.horizontalCenter = 'left';
    dateAxis.dateFormats.setKey('hour', dateFormat);
    dateAxis.dateFormats.setKey('day', dateFormat);
    dateAxis.periodChangeDateFormats.setKey('hour', `[bold] ${dateFormat}`);
    dateAxis.periodChangeDateFormats.setKey('day', `[bold] ${dateFormat}`);
    dateAxis.renderer.minGridDistance = 20;
    dateAxis.renderer.grid.template.location = 0.5;
    dateAxis.renderer.ticks.template.length = 8;
    dateAxis.renderer.ticks.template.strokeOpacity = 0.1;
    dateAxis.renderer.ticks.template.disabled = true;

    // these two lines makes the axis to be initially zoomed-in
    //dateAxis.start = 0.7;
    //dateAxis.keepSelection = true;

    var valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.tooltip.disabled = true;
    valueAxis.zIndex = 1;
    valueAxis.renderer.baseGrid.disabled = true;
    // Set up axis
    valueAxis.renderer.inside = true;
    valueAxis.height = am4core.percent(40);
    valueAxis.renderer.labels.template.verticalCenter = 'bottom';
    valueAxis.renderer.labels.template.padding(2, 2, 2, 2);
    valueAxis.renderer.fontSize = '0.8em';

    // uncomment these lines to fill plot area of this axis with some color
    valueAxis.renderer.gridContainer.background.fill = interfaceColors.getFor(
      'alternativeBackground',
    );
    valueAxis.renderer.gridContainer.background.fillOpacity = 0.05;

    var valueAxis2 = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis2.tooltip.disabled = true;
    // this makes gap between panels
    valueAxis2.marginTop = 50;
    valueAxis2.renderer.baseGrid.disabled = true;
    valueAxis2.renderer.inside = true;
    valueAxis2.height = am4core.percent(45);
    valueAxis2.zIndex = 3;
    valueAxis2.renderer.labels.template.verticalCenter = 'bottom';
    valueAxis2.renderer.labels.template.padding(2, 2, 2, 2);
    valueAxis2.renderer.fontSize = '0.8em';

    // uncomment these lines to fill plot area of this axis with some color
    valueAxis2.renderer.gridContainer.background.fill = interfaceColors.getFor(
      'alternativeBackground',
    );
    valueAxis2.renderer.gridContainer.background.fillOpacity = 0.05;

    let candleSeries = chart.series.push(new am4charts.CandlestickSeries());
    candleSeries.columns.template.width = am4core.percent(60);
    candleSeries.dropFromOpenState.properties.fill = am4core.color('#67DC75');
    candleSeries.dropFromOpenState.properties.stroke = am4core.color('#67DC75');
    candleSeries.riseFromOpenState.properties.fill = am4core.color('#67DC75');
    candleSeries.riseFromOpenState.properties.stroke = am4core.color('#67DC75');
    // candleSeries.columns.template.fill = am4core.color('#80FFFF');
    candleSeries.strokeWidth = 5;
    candleSeries.clustered = false;
    candleSeries.dataFields.dateX = 'timeStamp';
    candleSeries.dataFields.valueY = 'stdHigh'; //close price
    candleSeries.dataFields.openValueY = 'stdLow'; //open price
    candleSeries.dataFields.lowValueY = 'minValue'; // low price
    candleSeries.dataFields.highValueY = 'maxValue'; //high price
    candleSeries.simplifiedProcessing = true;
    candleSeries.tooltipText =
      '{dateX.formatDate("MM/dd/yyyy h:mm a")} \nStd Low: [bold]{openValueY.value} {unitsTotal}[/]\nMin Value: [bold]{lowValueY.value} {units}[/]\nMax Value: [bold]{highValueY.value} {units}[/]\nStd High: [bold]{valueY.value} {unitsTotal}[/]\nAvg Value: [bold]{avgValue} {units}[/]';

    let series = chart.series.push(new am4charts.LineSeries());
    series.dataFields.dateX = 'timeStamp';
    series.dataFields.valueY = 'avgValue';
    series.name = 'Average';
    const bullet = series.bullets.push(new am4charts.CircleBullet());
    bullet.circle.stroke = am4core.color('#fff');
    bullet.circle.strokeWidth = 2;

    let columnSeries = chart.series.push(new am4charts.ColumnSeries());
    columnSeries.columns.template.width = am4core.percent(60);
    // columnSeries.columns.template.stroke = am4core.color('#80FFFF');
    // columnSeries.columns.template.fill = am4core.color('#80FFFF');
    columnSeries.clustered = false;
    columnSeries.dataFields.dateX = 'timeStamp';
    columnSeries.dataFields.valueY = 'sum';
    columnSeries.yAxis = valueAxis2;
    columnSeries.tooltipText =
      '{dateX.formatDate("MM/dd/yyyy h:mm a")} \nTotal: [bold]{valueY} {unitsTotal}[/]';

    // var topContainer = chart.chartContainer.createChild(am4core.Container);
    // topContainer.layout = 'absolute';
    // topContainer.toBack();
    // topContainer.paddingBottom = 15;
    // topContainer.width = am4core.percent(100);

    // var label = topContainer.createChild(am4core.Label);
    // label.text =
    //   'Temp C value Avg: 20.95 Min: 20.71 Low: 20.95 High: 20.95 Max: 21.23';
    // label.fontWeight = 600;
    // label.align = 'left';
    // label.paddingLeft = 10;

    // // var label1 = chart.createChild(am4core.Label);
    // // label1.text = 'C Total 0.14423';
    // // label1.fontWeight = 600;
    // // label1.isMeasured = false;
    // // label1.x = 0;
    // // label1.y = am4core.percent(50);
    // // label1.align = 'left';
    // // label1.paddingLeft = 10;

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.xAxis = dateAxis;

    let scrollbarX = new am4charts.XYChartScrollbar();
    // scrollbarX.series.push(series);
    // scrollbarX.series.push(candleSeries);
    scrollbarX.series.push(columnSeries);
    scrollbarX.marginBottom = 20;
    scrollbarX.scrollbarChart.xAxes.getIndex(
      0,
    ).renderer.labels.template.disabled = true;
    scrollbarX.scrollbarChart.xAxes.getIndex(
      0,
    ).renderer.grid.template.disabled = true;
    chart.scrollbarX = scrollbarX;

    let selector = new am4plugins_rangeSelector.DateAxisRangeSelector();
    selector.container = document.getElementById('selectordiv');
    selector.inputDateFormat = 'MM/dd/yyyy HH:mm:ss';
    selector.periods = [
      {
        name: 'Hour',
        interval: { timeUnit: 'hour', count: 1 },
      },
      {
        name: 'Day',
        interval: { timeUnit: 'day', count: 1 },
      },
      {
        name: 'Month',
        interval: { timeUnit: 'month', count: 1 },
      },
      {
        name: 'Year',
        interval: { timeUnit: 'year', count: 1 },
      },
      {
        name: 'MAX',
        interval: 'max',
      },
    ];
    selector.axis = dateAxis;

    chart.exporting.menu = new am4core.ExportMenu();
    chart.exporting.menu.items[0].icon = exportIcon;
    chart.exporting.dateFields.push('timeStamp');
    chart.exporting.dateFormat = dateFormat;
    chart.exporting.filePrefix = 'StatisticsDetail-Chart';
  }
  return (
    <div>
      <h1 className="text-center">{data.name}</h1>
      {/* {data.length > 2000 && (
        <span className="color-red" style={{ fontSize: 20 }}>
          {`${t('Attention')}: ${t(
            'Results show only first 2000 data points for range selected',
          )}`}
        </span>
      )} */}
      <div
        id="chartArea"
        className="chartArea"
        style={{ width: '100%', height: 900 }}
      />
      <div id="selectordiv" />
    </div>
  );
}

function mapStateToProps(state) {
  return {
    report: state.report,
  };
}

export default withTranslation('app')(connect(mapStateToProps, null)(Chart));
